import { MouseEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as BarcodeBlack } from "../../assets/images/icon/barcode-black.svg"
import { ReactComponent as BarcodeWhite } from "../../assets/images/icon/barcode-white.svg"
import { ReactComponent as BarcodeV2 } from "../../assets/images/icon/barcode-v2.svg"
import { LoadingOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { isLogin } from "../../utils";
import { BarcodeBasicModal, BarcodeVipModal } from "../../shared";
import { getMemberDataByToken, hasReservationToday, getLocalAccessToken } from "../../service";

interface IProps {
    color?: 'white' | 'black' | string
    style?: React.CSSProperties;
    onClick?: (event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => void;
    setPropsState?: React.Dispatch<React.SetStateAction<boolean>>;
    propsOpen?: boolean
    isNotModal?: boolean
    isOnlyModal?: boolean
}

const BarcodeIconWithModal = ({ color, style, onClick, setPropsState, propsOpen, isNotModal, isOnlyModal }: IProps) => {
    const [isOpenBarcodeModal, setIsOpenBarcodeModal] = useState<boolean>(false);
    const accessToken = getLocalAccessToken();
    const isLoggedIn = isLogin();
    const isModalDataEnabled = isLoggedIn && isOpenBarcodeModal;

    const { data: member, isLoading: isMemberLoading, isError: isMemberError } = useQuery({
        queryKey: ['memberByToken', accessToken],
        queryFn: () => getMemberDataByToken().then(res => res.data),
        enabled: isModalDataEnabled,
    });
    const { data: hasReservationTodayData, isLoading: isReservationLoading, isError: isReservationError } = useQuery({
        queryKey: ['hasReservationToday', accessToken],
        queryFn: () => hasReservationToday().then(res => res.data),
        enabled: isModalDataEnabled,
        gcTime: 0,
    });

    const barcodeOnClick = (event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
        setPropsState?.(true)
        onClick?.(event)
        setIsOpenBarcodeModal(true)
    }
    const setIsOpen = (isOpen: boolean) => {
        setPropsState?.(isOpen)
        setIsOpenBarcodeModal(isOpen)
    }

    const BarcodeToColor = ({ color }: { color?: 'white' | 'black' | string }) => {
        switch (color) {
            case 'white':
                return <BarcodeWhite style={{ cursor: 'pointer', ...style }} onClick={barcodeOnClick} />
            case 'black':
                return <BarcodeBlack style={{ cursor: 'pointer', ...style }} onClick={barcodeOnClick} />
            case undefined:
                return <BarcodeBlack style={{ cursor: 'pointer', ...style }} onClick={barcodeOnClick} />
            default:
                return <BarcodeV2 fill={color} style={{ cursor: 'pointer', ...style }} onClick={barcodeOnClick} />
        }
    }

    useEffect(() => {
        if (propsOpen === undefined) return;
        setIsOpen(propsOpen)
    }, [propsOpen])

    const isModalLoading = isOpenBarcodeModal && (isMemberLoading || isReservationLoading);
    const isVip = member?.grade === 'VIP';

    const backdrop = isOpenBarcodeModal
        ? createPortal(
            <div
                onClick={() => setIsOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 999, backgroundColor: 'rgba(0, 0, 0, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                {isModalLoading && <LoadingOutlined spin style={{ fontSize: 28, color: 'var(--basewhite)' }} />}
            </div>,
            document.body,
        )
        : null;

    const barcodeModal = isModalLoading
        ? null
        : isVip && member
            ? <BarcodeVipModal
                isOpen={isOpenBarcodeModal}
                setIsOpen={setIsOpen}
                member={member}
                hasReservationToday={hasReservationTodayData}
                isReservationError={isReservationError}
            />
            : <BarcodeBasicModal
                isOpen={isOpenBarcodeModal}
                setIsOpen={setIsOpen}
                member={member}
                isError={isMemberError || isReservationError}
                hasReservationToday={hasReservationTodayData}
            />;

    return <>
        {!isOnlyModal && <BarcodeToColor color={color} />}
        {!isNotModal && isLoggedIn && <>{backdrop}{barcodeModal}</>}
    </>
}

export { BarcodeIconWithModal };
